import React, { useCallback, useContext, useEffect, useState } from "react";

import LoadingButton from "@mui/lab/LoadingButton";
import { Grid, TextField } from "@mui/material";

import { Region } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/workflows_pb";

import { DEFAULT_REGION_CODE } from "../../constants/index";
import RegionSelector from "./shared/region-selector";
import UpdateInspectionTitlesInfoCard from "./update-inspection-title-info-card";
import { bulkUpdateInspectionTitle } from "api/endpoints/workflows";
import { callOptionsHeaders } from "api/helpers";
import { SnackbarContext } from "context/snackbar-context";
import { getScUserEmail } from "helpers/get-sc-user-email";
import { getScUserName } from "helpers/get-sc-user-name";

interface UpdateInspectionTitlesFormProps {
  listSupportWorkflows: () => void;
}

const UpdateInspectionTitlesForm = ({
  listSupportWorkflows,
}: UpdateInspectionTitlesFormProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [formState, setFormState] = useState({
    inspectionIds: "",
    inspectionTitleRequest: "",
    templateId: "",
    inspectionOrgId: "",
  });

  const { showError } = useContext(SnackbarContext);

  const [scUserName, setScUserName] = useState("");
  const [scUserEmail, setScUserEmail] = useState("");

  const [targetRegion, setTargetRegion] = useState<Region>();

  const updateInspectionTitles = useCallback(
    (region: Region) => {
      setIsLoading(true);
      bulkUpdateInspectionTitle(
        {
          inspectionList: formState.inspectionIds
            .replace(/^(?=\n)$|^\s*|\s*$|\n\n+/gm, "")
            .split(/\r?\n/),
          region: region,
          employeeName: scUserName,
          employeeEmail: scUserEmail,
          inspectionTitleRequestReference: formState.inspectionTitleRequest,
          templateId:
            formState.templateId.trim() !== ""
              ? formState.templateId
              : undefined,
          inspectionOrgId:
            formState.inspectionOrgId.trim() !== ""
              ? formState.inspectionOrgId
              : undefined,
        },
        callOptionsHeaders(DEFAULT_REGION_CODE)
      )
        .then(() => {
          setFormState({
            inspectionIds: "",
            inspectionTitleRequest: "",
            templateId: "",
            inspectionOrgId: "",
          });
          listSupportWorkflows();
        })
        .catch(showError)
        .finally(() => {
          setIsLoading(false);
        });
    },
    [
      formState.inspectionIds,
      formState.inspectionTitleRequest,
      listSupportWorkflows,
      scUserEmail,
      scUserName,
      showError,
      formState.templateId,
      formState.inspectionOrgId,
    ]
  );

  const handleTextAreaChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    key: string
  ) => {
    setFormState({
      ...formState,
      [key]: event.target.value,
    });
  };

  // Determine if inspection IDs mode is active
  const isInspectionIdsMode = formState.inspectionIds.trim() !== "";

  // Determine if template mode is active
  const isTemplateIdMode =
    formState.templateId.trim() !== "" ||
    formState.inspectionOrgId.trim() !== "";

  useEffect(() => {
    getScUserName().then((name) => setScUserName(name));
    getScUserEmail().then((email) => setScUserEmail(email));
  }, []);

  return (
    <>
      <Grid item xs={12}>
        <form
          style={{ display: "flex", flexDirection: "column", gap: "16px" }}
          noValidate
          autoComplete="off"
        >
          <Grid
            container
            spacing={2}
            alignItems="center"
            justifyContent="center"
          >
            <Grid item>
              <TextField
                id="customer-request-input"
                label="Customer Request"
                value={formState.inspectionTitleRequest}
                onChange={(event) =>
                  handleTextAreaChange(event, "inspectionTitleRequest")
                }
                variant="outlined"
                size="small"
                disabled={isLoading}
                inputProps={{ "data-testid": "customer-request-input" }}
                style={{ minWidth: 300 }}
              />
            </Grid>
            <Grid item>
              <RegionSelector
                targetRegion={targetRegion}
                setTargetRegion={setTargetRegion}
                isLoading={isLoading}
              />
            </Grid>
          </Grid>
          <Grid
            container
            spacing={2}
            alignItems="center"
            justifyContent="center"
            style={{ marginTop: 16 }}
          >
            <Grid item>
              <TextField
                id="inspection-id-input"
                label="Inspection IDs"
                multiline
                maxRows={10}
                value={formState.inspectionIds}
                onChange={(event) =>
                  handleTextAreaChange(event, "inspectionIds")
                }
                variant="outlined"
                size="small"
                disabled={isLoading || isTemplateIdMode}
                inputProps={{ "data-testid": "inspection-id-input" }}
                style={{ minWidth: 400 }}
              />
            </Grid>
            <Grid item>
              <TextField
                id="template-id-input"
                label="Template ID"
                value={formState.templateId}
                onChange={(event) => handleTextAreaChange(event, "templateId")}
                variant="outlined"
                size="small"
                disabled={isLoading || isInspectionIdsMode}
                inputProps={{ "data-testid": "template-id-input" }}
                style={{ minWidth: 300 }}
              />
            </Grid>
            <Grid item>
              <TextField
                id="inspection-org-id-input"
                label="Inspection Org ID"
                value={formState.inspectionOrgId}
                onChange={(event) =>
                  handleTextAreaChange(event, "inspectionOrgId")
                }
                variant="outlined"
                size="small"
                disabled={isLoading || isInspectionIdsMode}
                inputProps={{ "data-testid": "inspection-org-id-input" }}
                style={{ minWidth: 300 }}
              />
            </Grid>
            <Grid item>
              <LoadingButton
                variant="contained"
                color="primary"
                size="large"
                onClick={() => {
                  updateInspectionTitles(targetRegion as Region);
                }}
                loading={isLoading}
                disabled={
                  formState.inspectionTitleRequest === "" ||
                  targetRegion === undefined ||
                  (formState.inspectionIds === "" &&
                    (formState.templateId === "" ||
                      formState.inspectionOrgId === ""))
                    ? true
                    : false
                }
                data-testid="execute-workflow-button"
              >
                Execute Workflow
              </LoadingButton>
            </Grid>
          </Grid>
        </form>
      </Grid>

      <Grid item xs={12} style={{ paddingBottom: "20px" }}>
        <Grid container alignContent="space-between" alignItems="center">
          <UpdateInspectionTitlesInfoCard />
        </Grid>
      </Grid>
    </>
  );
};

export default UpdateInspectionTitlesForm;
